import {
    CognitoAccessToken,
    CognitoIdToken,
    CognitoRefreshToken,
    CognitoUser,
    CognitoUserSession,
    ICognitoUserData,
    ICognitoUserSessionData,
    ICognitoUserPoolData,
    CognitoUserPool
} from 'amazon-cognito-identity-js';
import { AppConfig } from '../app.config';
import { CognitoUtil } from './cognito.service';
import { MemoryUserCredentialsDataStorage, parseJWT } from './jwt-util';
import { UserCredentials } from './user-credentials';
import * as AWS from 'aws-sdk/global';
import { TenantInfo } from '../tenant/tenant-info';
import * as CognitoIdentity from 'aws-sdk/clients/cognitoidentity';

require('amazon-cognito-js');


export const TOKEN_REVOKED = 'TOKEN_REVOKED'

export class UserCredentialsCognito implements UserCredentials {
    private _POOL_DATA;
    public cognitoCreds: AWS.CognitoIdentityCredentials;
    private tenantInfo: TenantInfo;

    constructor(
        private appConfig: AppConfig) {
        this.tenantInfo = appConfig.getTenantInfo();
        AWS.config.region = this.tenantInfo.region;
        this._POOL_DATA = {
            UserPoolId: this.tenantInfo.userPoolId,
            ClientId: this.tenantInfo.clientId,
            Storage: window.sessionStorage
            // Storage: new MemoryUserPoolDataStorage()
        } as ICognitoUserPoolData;
    }

    handleJWTTokens(idToken: string, accessToken: string, refreshToken: string): void {
        const sessionData = {
            IdToken: new CognitoIdToken({ IdToken: idToken }),
            AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
            RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken })
        } as ICognitoUserSessionData;
        const session = new CognitoUserSession(sessionData);
        const pool = this.getUserPool();
        const idPayload = parseJWT(idToken);
        const userData = {
            Username: idPayload.email,
            Pool: pool,
            Storage: window.sessionStorage,
        } as ICognitoUserData;
        this.switchToMemoryStorage();
        const cognitoUser = new CognitoUser(userData);
        cognitoUser.setSignInUserSession(session);
    }

    getUserName(): string {
        return this.getCurrentUser().getUsername();
    }


    private async getTokenFromRefreshedSession(refreshToken: CognitoRefreshToken, type: 'ACCESS' | 'ID'): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const cognitoUser = this.getCurrentUser();
            cognitoUser.refreshSession(refreshToken, (err, session: CognitoUserSession) => {
                if (err) {
                    reject(err);
                } else {
                    if (type === 'ACCESS') {
                        resolve(session.getAccessToken().getJwtToken());
                    } else {
                        resolve(session.getIdToken().getJwtToken());
                    }
                }
            });
        });
    }


    buildCognitoCreds(idTokenJwt: string, tenantInfo: TenantInfo) {
        const url = 'cognito-idp.' + tenantInfo.region.toLowerCase() + '.amazonaws.com/' + tenantInfo.userPoolId;
        const logins: CognitoIdentity.LoginsMap = {};
        logins[url] = idTokenJwt;
        const params = {
            IdentityPoolId: tenantInfo.identityPoolId, /* required */
            Logins: logins
        };
        const creds = new AWS.CognitoIdentityCredentials(params);
        return creds;
    }


    getCognitoIdentity(): string {
        return this.cognitoCreds.identityId;
    }

    private async getJWTToken(type: 'ACCESS' | 'ID'): Promise<string> {
        if (this.appConfig.getUseJSONData()) {
            return 'TokenForJSONData';
        }
        const session = await this.getSession();
        let token;
        if (session.isValid()) {
            if (type === 'ACCESS') {
                token = session.getAccessToken().getJwtToken();
            } else {
                token = session.getIdToken().getJwtToken();
            }
        } else {
            try {
                const refreshToken = await this.getCognitoRefreshToken();
                if (!refreshToken) {
                    throw new Error('Could not retrieve refresh token');
                } else {
                    token = await this.getTokenFromRefreshedSession(refreshToken, type);
                }
            } catch (err) {
                console.log(`RefreshToken error: ${JSON.stringify(err)}`);
                if (err.message == 'Refresh Token has been revoked') {
                    return TOKEN_REVOKED;
                }
            }
        }
        let resetCredentials = false;
        const currentCreds = AWS.config.credentials;
        if (!currentCreds) {
            resetCredentials = true;
        } else {
            if (currentCreds instanceof AWS.Credentials) {
                const currentCredsCredintials = currentCreds as AWS.Credentials;
                if (currentCredsCredintials.needsRefresh()) {
                    resetCredentials = true;
                }
            }
        }
        if (resetCredentials) {
            // Set the AWS Creditials object logins to refresh the tokens
            // there too so that the web identify federated login requests work too
            const creds = this.buildCognitoCreds(token, this.tenantInfo);
            this.setCognitoCreds(creds);
            AWS.config.credentials = creds;
        }
        return token;
    }

    async getIdToken(): Promise<string> {
        return this.getJWTToken('ID');
    }

    async getAccessToken(): Promise<string> {
        return this.getJWTToken('ACCESS');
    }


    async getCognitoRefreshToken(): Promise<CognitoRefreshToken> {
        const session = await this.getSession();
        if (session.isValid()) {
            return session.getRefreshToken();
        }
        throw new Error('No valid session');
    }

    async getRefreshToken(): Promise<string> {
        const cognitoRefreshToken = await this.getCognitoRefreshToken();
        return cognitoRefreshToken.getToken();
    }


    getCurrentUser() {
        return this.getUserPool().getCurrentUser();
    }

    async getSession(): Promise<CognitoUserSession> {
        return new Promise<CognitoUserSession>((resolve, reject) => {
            const cognitoUser = this.getCurrentUser();
            if (!cognitoUser) {
                reject(new Error('No Cognito User available'));
            }
            cognitoUser.getSession((err, session: CognitoUserSession) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(session);
                }
            });
        });
    }


    async getCurrentUserWithSession(): Promise<CognitoUser> {
        return new Promise((resolve, reject) => {
            const currentUser = this.getCurrentUser();
            if (currentUser === null) {
                resolve(undefined);
            }
            currentUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(currentUser);
                }
            });
        });
    }

    async handleGlobalLogout(): Promise<string> {
        // Need to call this so globalSignout can find a session
        const currentUser = await this.getCurrentUserWithSession();
        if (currentUser) {
            await new Promise((resolve, reject) => {
                currentUser.globalSignOut({
                    onSuccess: (msg: string) => {
                        console.log(`UserLoginService: Globally signed out: ${msg}`);
                        resolve(msg)
                    },
                    onFailure: (err: Error) => {
                        reject(err);
                    }
                });
            });
            // await new Promise((resolve, reject) => {
            currentUser.signOut(() => {
                console.log('Signed out');
                return Promise.resolve();
            });
        }
        // });
        // const cisp = new AWS.CognitoIdentityServiceProvider();
        // await cisp.revokeToken({
        //     Token: refreshToken.getToken(),
        //     ClientId: clientId
        // } as AWS.CognitoIdentityServiceProvider.Types.RevokeTokenRequest).promise();

        if (AWS.config.credentials && AWS.config.credentials instanceof AWS.CognitoIdentityCredentials) {
            const cognitoCredentials: AWS.CognitoIdentityCredentials = AWS.config.credentials;
            cognitoCredentials.clearCachedId();
        }
        const clientId = this.getUserPool().getClientId();
        const lastUserKey = 'CognitoIdentityServiceProvider.' + clientId + '.LastAuthUser';
        window.localStorage.removeItem(lastUserKey);
        window.sessionStorage.removeItem(lastUserKey);
        window.sessionStorage.removeItem('crossSessionGlobalLogoutPerformed');
        return 'Signed out';
    }

    isAuthenticated(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const cognitoUser = this.getCurrentUser();
            if (cognitoUser != null) {
                cognitoUser.getSession(function (err, session) {
                    if (err) {
                        reject(`UserLoginService: Couldn't get the session: ${err}`);
                    } else {
                        if (session.isValid()) {
                            resolve(true);
                        } else {
                            reject(`UserLoginService: Session is ${session.isValid()}`);
                        }
                    }
                });
            } else {
                console.log(`UserLoginService: can't retrieve the current user`);
                reject(`Can't retrieve the CurrentUser`);
            }
        });
    }


    getUserPool() {
        return new CognitoUserPool(this._POOL_DATA);
    }


    switchToMemoryStorage() {
        const memoryStorage = new MemoryUserCredentialsDataStorage();
        this._POOL_DATA.Storage = memoryStorage;
        const cognitoKeys: string[] = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
            const key = window.sessionStorage.key(i);
            if (key.startsWith('CognitoIdentityServiceProvider')) {
                memoryStorage.setItem(key, window.sessionStorage[key]);
                cognitoKeys.push(key);
            }
        }
        // Clear out  keys with cognito token info from session storage
        for (let i = 0; i < cognitoKeys.length; i++) {
            window.sessionStorage.removeItem(cognitoKeys[i]);
        }
    }

    // AWS Stores Credentials in many ways, and with TypeScript this means that
    // getting the base credentials we authenticated with from the AWS globals gets really murky,
    // having to get around both class extension and unions. Therefore, we're going to give
    // developers direct access to the raw, unadulterated CognitoIdentityCredentials
    // object at all times.
    setCognitoCreds(creds: AWS.CognitoIdentityCredentials) {
        this.cognitoCreds = creds;
    }

    getCognitoCreds() {
        return this.cognitoCreds;
    }

}